// Anonymous usage pings from the signed-out page (the landing, with sign-up and sign-in), for the Superintelect
// admin's "On the page" (README → Usage stats). A ping is an empty POST /e?p=<page>&e=<event>&k=<key> that Caddy
// answers 204 and logs; the admin counts it from that log, once per visitor and day, next to the visits. Nothing is
// stored in the browser and no id is sent; each event goes once per page load (the page may mount more than once).
// Signed in, nothing is sent.
// Events: view · lang · sec (a section reached) · stay (time on screen) · click (a link or button, by where it sits and
// its words) · and, from the page through track(): film (the promo with sound), form (sign-up, sign-in, reset).

let page = "";
const sent = new Set<string>();

export function track(e: string, k = "") {
  if (!page) return;
  k = k.replace(/\s+/g, " ").trim().slice(0, 60).trim();
  if (sent.has(`${page}|${e}|${k}`)) return;
  sent.add(`${page}|${e}|${k}`);
  const url = `/e?${new URLSearchParams(k ? { p: page, e, k } : { p: page, e })}`;
  try {
    if (!navigator.sendBeacon(url)) throw new Error("queue full");
  } catch {
    fetch(url, { method: "POST", keepalive: true }).catch(() => {});
  }
}

/** An element's words, its parts kept apart. */
function words(el: Element) {
  const out: string[] = [],
    walk = document.createTreeWalker(el, NodeFilter.SHOW_TEXT);
  while (walk.nextNode()) out.push(walk.currentNode.nodeValue || "");
  return out.join(" ").replace(/\s+/g, " ").trim();
}
const name = (el: Element) =>
  el.id ||
  (el.getAttribute("aria-labelledby") || "").replace(/-h$/, "") ||
  el.classList[el.classList.length - 1] ||
  el.tagName.toLowerCase();

/**
 * Starts the pings for one signed-out page: its view and language, the sections reached, the time on screen and the
 * presses. Returns the stop (on sign-in, or leaving the page).
 */
export function watchPage(path: string, lang: string) {
  page = path;
  track("view");
  track("lang", lang);

  // sections: reached once on screen with their top above 60 % of it, or the page scrolled to its end
  const secs = [...document.querySelectorAll("section")]
    .filter((s) => !s.parentElement?.closest("section"))
    .map((el) => ({
      el,
      top: el.getBoundingClientRect().height
        ? el.getBoundingClientRect().top
        : Infinity,
      k: "",
      done: false,
    }));
  [...secs]
    .sort((a, b) => a.top - b.top)
    .forEach(
      (s, i) => (s.k = `${String(i + 1).padStart(2, "0")} ${name(s.el)}`),
    );
  let queued = false;
  const reach = () => {
    queued = false;
    const end =
      innerHeight + scrollY >= document.documentElement.scrollHeight - 2;
    for (const s of secs) {
      if (s.done) continue;
      const r = s.el.getBoundingClientRect();
      if (r.height && r.bottom > 0 && r.top < innerHeight * (end ? 1 : 0.6)) {
        s.done = true;
        track("sec", s.k);
      }
    }
  };
  const onScroll = () => {
    if (!queued) {
      queued = true;
      requestAnimationFrame(reach);
    }
  };
  addEventListener("scroll", onScroll, { passive: true });
  addEventListener("resize", onScroll);
  reach();

  // time on screen, counted only while the tab is visible
  const STAY: Record<number, string> = {
    10: "00:10",
    30: "00:30",
    60: "01:00",
    180: "03:00",
  };
  let on = 0;
  const clock = setInterval(() => {
    if (document.visibilityState !== "visible") return;
    if (STAY[++on]) track("stay", STAY[on]);
    if (on >= 180) clearInterval(clock);
  }, 1000);

  // presses: links and buttons, by the section (or header) they sit in and what they say
  const onClick = (ev: MouseEvent) => {
    const el = (ev.target as Element | null)?.closest?.("a, button");
    if (!el) return;
    const sec = secs.find((s) => s.el.contains(el));
    const area =
      el.closest("[id]:not(#root)")?.id ||
      el.closest("header, nav, main, footer")?.tagName.toLowerCase();
    const where = sec ? name(sec.el) : area || "page";
    const label =
      (el as HTMLElement).dataset.ev ||
      el.getAttribute("aria-label") ||
      words(el) ||
      el.getAttribute("href") ||
      "?";
    track("click", `${where}: ${label}`);
  };
  document.addEventListener("click", onClick, true);

  return () => {
    removeEventListener("scroll", onScroll);
    removeEventListener("resize", onScroll);
    document.removeEventListener("click", onClick, true);
    clearInterval(clock);
    page = "";
  };
}
